import { CameraView, useCameraPermissions, type BarcodeScanningResult } from 'expo-camera';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';

import { BRAND } from '@sixam/config/brand';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { parseMemberQrJwt, type ParsedMemberQrJwt } from '@/lib/member-qr';

/** Ink behind the viewfinder so letterboxing reads as part of the camera. */
const viewfinderBackdrop = { backgroundColor: BRAND.color.ink } as const;

type MemberQrScannerProps = {
  active: boolean;
  /**
   * Fill the parent edge to edge instead of sitting in a bordered square card.
   * Used by the full-screen check-in station, where the viewfinder is the screen.
   */
  fill?: boolean;
  onScanned: (scan: ParsedMemberQrJwt) => void;
  /**
   * Changing this re-arms the scanner for another scan. Leave undefined for a
   * one-shot scan (the scanner stops after the first read); a station bumps it
   * after each scan to keep scanning runners in a row.
   */
  rearmToken?: number;
};

/**
 * The two shapes this scanner takes. Resolved once up front so the component
 * itself reads as one flow rather than a ternary at every element.
 */
function viewfinderPresentation(fill: boolean | undefined) {
  if (fill) {
    return {
      backdrop: viewfinderBackdrop,
      body: 'text-white/80',
      bodyVariant: undefined,
      camera: styles.cameraFill,
      frame: 'flex-1',
      pending: 'flex-1',
      permission: 'flex-1 justify-center gap-4 p-5',
      title: 'text-white',
    } as const;
  }

  return {
    backdrop: undefined,
    body: undefined,
    bodyVariant: 'muted',
    camera: styles.camera,
    frame: 'overflow-hidden rounded-md border-2 border-border bg-card',
    pending: 'h-80 rounded-md bg-muted',
    permission: 'gap-4 rounded-md border-2 border-border bg-card p-5',
    title: undefined,
  } as const;
}

export function MemberQrScanner({
  active,
  fill,
  onScanned,
  rearmToken,
}: Readonly<MemberQrScannerProps>) {
  const look = viewfinderPresentation(fill);
  const [permission, requestPermission] = useCameraPermissions();
  // A ref lock (not state) so the camera's rapid repeat callbacks for one code
  // only fire onScanned once, without a re-render or a setState-in-effect.
  const scanLockRef = React.useRef(false);

  React.useEffect(() => {
    // Re-arm for the next scan whenever the parent bumps the token. Undefined
    // token never changes, so the scanner stays one-shot.
    scanLockRef.current = false;
  }, [rearmToken]);

  const handleBarcodeScanned = React.useCallback(
    (result: BarcodeScanningResult) => {
      if (scanLockRef.current) {
        return;
      }
      scanLockRef.current = true;
      onScanned(parseMemberQrJwt(result.data));
    },
    [onScanned]
  );

  if (!permission) {
    return <View className={look.pending} style={look.backdrop} />;
  }

  if (!permission.granted) {
    return (
      <View className={look.permission} style={look.backdrop}>
        <Text className={look.title} variant="large">
          Camera access
        </Text>
        <Text className={look.body} variant={look.bodyVariant}>
          Allow camera access to scan another member QR code.
        </Text>
        <Button onPress={requestPermission}>
          <Text>Allow camera</Text>
        </Button>
      </View>
    );
  }

  return (
    <View className={look.frame} style={look.backdrop}>
      <CameraView
        active={active}
        barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
        facing="back"
        onBarcodeScanned={active ? handleBarcodeScanned : undefined}
        // The station's controls live around this native preview. Scanning does
        // not need touch input, so keep the preview out of the responder chain.
        pointerEvents="none"
        style={look.camera}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  camera: {
    aspectRatio: 1,
    width: '100%',
  },
  cameraFill: {
    flex: 1,
  },
});
